import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_INTENTS = ["general", "logistics", "properties", "government", "partnership", "diversity"];

const initial = {
  full_name: "",
  email: "",
  company: "",
  phone: "",
  inquiry_type: "general",
  message: "",
};

export default function Contact() {
  useSEO({
    title: "Contact Tandem T Holdings LLC",
    description: "Contact Tandem T Holdings LLC — logistics, real estate, government contracting, and partnership inquiries. Tampa, FL headquarters. Founded by Chrissy Thompson.",
    path: "/contact",
  });
  const [searchParams] = useSearchParams();
  const intent = searchParams.get("intent");
  const preselected = VALID_INTENTS.includes(intent) ? intent : "general";

  const [form, setForm] = useState({ ...initial, inquiry_type: preselected });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.length < 2) return toast.error("Please enter your full name.");
    if (!form.email.includes("@")) return toast.error("Please enter a valid email.");
    if (form.message.length < 10) return toast.error("Please add a few more details to your message.");

    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry received — our team will respond shortly.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "rounded-none bg-transparent border-0 border-b border-white/25 focus-visible:ring-0 focus-visible:border-white px-0 py-6 text-white placeholder:text-white/30 text-base";

  return (
    <div data-testid="page-contact" className="pt-32 md:pt-40 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* LEFT — copy + info */}
        <div className="md:col-span-5">
          <Reveal>
            <p className="overline text-white/55">Engage Tandem T Holdings</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif font-light text-white text-5xl md:text-7xl leading-[1] tracking-tight mt-6">
              Let's <em className="italic">build.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-white/70 font-light leading-relaxed mt-8 max-w-md">
              Whether you're a contracting officer, prime, lender, tenant, or strategic partner — share a
              few details and the office of the CEO will route your inquiry to the right division.
            </p>
          </Reveal>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <Mail size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Email</p>
                <a href="mailto:partnerships@tandemtholdings.com" className="text-white/90 mt-1 block hover:text-white" data-testid="contact-email">partnerships@tandemtholdings.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Phone</p>
                <a href="tel:+16562566532" className="text-white/90 mt-1 block hover:text-white" data-testid="contact-phone">(656) 256-6532</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Corporate Headquarters</p>
                <p className="text-white/90 mt-1" data-testid="contact-address">
                  550 N. Reo St., Suite 300<br />
                  Tampa, FL 33609
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Send size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Web</p>
                <p className="text-white/90 mt-1">www.tandemtholdings.com</p>
              </div>
            </div>
          </div>

          <div className="mt-14 border-t border-white/15 pt-8">
            <p className="overline text-white/45">Ownership</p>
            <p className="font-serif text-white text-2xl mt-3 font-light">Minority Woman-Owned · Founder-Led</p>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-white/15 p-8 md:p-12 bg-white/[0.02]"
            >
              <p className="overline text-white/55 mb-8">Inquiry Form</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div className="md:col-span-2">
                  <Label className="overline text-white/50">Full Name</Label>
                  <Input
                    data-testid="contact-input-name"
                    className={inputClass}
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Email</Label>
                  <Input
                    data-testid="contact-input-email"
                    type="email"
                    className={inputClass}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Phone</Label>
                  <Input
                    data-testid="contact-input-phone"
                    className={inputClass}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (555) 555-5555"
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Company / Agency</Label>
                  <Input
                    data-testid="contact-input-company"
                    className={inputClass}
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Organization"
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Inquiry Type</Label>
                  <Select
                    value={form.inquiry_type}
                    onValueChange={(v) => update("inquiry_type", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-select-type"
                      className="rounded-none bg-transparent border-0 border-b border-white/25 focus:ring-0 px-0 py-6 text-white"
                    >
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border border-white/20 rounded-none text-white">
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="logistics">Logistics / Freight</SelectItem>
                      <SelectItem value="properties">Real Estate</SelectItem>
                      <SelectItem value="government">Government Contracting</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="diversity">Supplier Diversity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 mt-4">
                  <Label className="overline text-white/50">Message</Label>
                  <Textarea
                    data-testid="contact-input-message"
                    rows={5}
                    className="rounded-none bg-transparent border border-white/25 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/30 mt-3"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about the opportunity, scope, timing or context…"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 mt-10 px-8 py-4 bg-white text-black overline hover:bg-white/85 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Submitting…" : "Send Inquiry"}
                <Send size={14} className="hover-arrow" />
              </button>

              {submitted && (
                <p data-testid="contact-success" className="mt-6 text-white/75 text-sm font-light">
                  Thank you — your inquiry has been received. A member of the Tandem T team will be in touch.
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
